import { Box, FormHelperText, Typography } from '@mui/material';
import type { FieldAdapterProps } from '../../../runtime/forms/field-adapter-registry';
import { formatFieldValue } from '../../../runtime/forms/field-formatter';
import { useFieldOptions } from '../../../runtime/forms/use-field-options';
import { resolveTKey, translateRuntimeTKey } from '../../../i18n/resolve-key';

export function ReadonlyValueAdapter(props: FieldAdapterProps) {
  const { field, formValues, serverError, t, describedBy } = props;
  const { options, loading } = useFieldOptions(field, formValues ?? {}, t);
  const label = t(resolveTKey(field.labelKey));
  const value = formValues?.[field.name];
  const displayValue = loading
    ? ''
    : (field.fieldType === 'checkbox' || field.fieldType === 'switch') && typeof value === 'boolean'
      ? t(value ? 'common:yes' : 'common:no')
      : formatFieldValue(field, value, options);
  const helpText = field.helpTextKey ? t(resolveTKey(field.helpTextKey)) : undefined;
  const displayServerError = serverError
    ? (translateRuntimeTKey(t, serverError) ?? t('common:error.unexpected'))
    : undefined;
  const internalHelp = displayServerError ?? helpText;
  const internalHelpId = internalHelp ? `${field.name}-readonly-helper` : undefined;
  const ariaDescribedBy = [describedBy, internalHelpId].filter(Boolean).join(' ') || undefined;

  return (
    <Box
      component="dl"
      data-testid="readonly-field-value"
      data-field-name={field.name}
      sx={{ m: 0, minWidth: 0, width: '100%' }}
    >
      <Typography
        component="dt"
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.5,
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: '0.01em',
          overflowWrap: 'anywhere',
        }}
      >
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{
          m: 0,
          px: 1.75,
          py: 1.25,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'action.disabledBackground',
          minHeight: 56,
          minWidth: 0,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          fontWeight: 500,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
        aria-describedby={ariaDescribedBy}
      >
        {displayValue}
      </Typography>
      {internalHelp && (
        <FormHelperText
          id={internalHelpId}
          error={Boolean(displayServerError)}
          sx={{ mt: 0.75, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        >
          {internalHelp}
        </FormHelperText>
      )}
    </Box>
  );
}
